import { value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { isServiceConfig } from "../util/config.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
import { getInitialData } from "../util/initial-data.js";
export const name = 'azureOpenai';
export const provider = {
    name,
    title: 'Azure OpenAI',
    description: 'Build your own copilot and generative AI applications.',
    categories: ['ai'],
    links: [
        {
            title: 'Homepage',
            url: 'https://azure.microsoft.com/en-us/products/ai-services/openai-service'
        },
        { title: 'Docs', url: '/docs/services/providers/azure-openai' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value(name),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'endpoint', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Azure OpenAI'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'azure-openai'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'AzureOpenAI'
            },
            endpoint: {
                title: 'Endpoint',
                type: 'string',
                pattern: '^https://[w-]+.openai.azure.com/openai/deployments/[w-/]+?api-version=[w-]+$'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'endpoint', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            endpoint: {
                widget: 'singleLineText',
                instructions: 'The endpoint for your deployed model.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'You can manage API keys for OpenAI at https://platform.openai.com/api-keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        let resource;
        let model;
        let apiVersion;
        if (formData.endpoint) {
            // e.g., https://takeshape-openai-test.openai.azure.com/openai/deployments/gpt-35-turbo/chat/completions?api-version=2024-08-01-preview
            const endpointUrl = new URL(formData.endpoint);
            // e.g., takeshape-openai-test.openai.azure.com -> takeshape-openai-test
            resource = endpointUrl.hostname.split('.')[0];
            // e.g., /openai/deployments/gpt-35-turbo/chat/completions -> gpt-35-turbo
            model = endpointUrl.pathname.split('/')[3];
            apiVersion = endpointUrl.searchParams.get('api-version') ?? undefined;
        }
        const config = {
            id: formData.id,
            title: formData.title,
            namespace: formData.namespace,
            provider: name,
            serviceType: 'openapi',
            authenticationType: 'bearer',
            options: {
                resource,
                model,
                apiVersion,
                spec: 'https://raw.githubusercontent.com/Azure/azure-rest-api-specs/refs/heads/main/specification/cognitiveservices/data-plane/AzureOpenAI/inference/stable/2024-10-21/inference.yaml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        return updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'api-key'
        });
    },
    getInitialData(serviceConfig) {
        const initialData = getInitialData(provider, serviceConfig);
        if (serviceConfig?.options?.resource && serviceConfig.options.model && serviceConfig.options.apiVersion) {
            initialData.endpoint = `https://${serviceConfig.options.resource}.openai.azure.com/openai/deployments/${serviceConfig.options.model}/chat/completions?api-version=${serviceConfig.options.apiVersion}`;
        }
        return initialData;
    },
    isCompleteConfig: hasEveryKey([
        'authentication',
        'options.spec',
        'options.resource',
        'options.model',
        'options.apiVersion'
    ]),
    isServiceConfig: isServiceConfig(name, [
        'authentication.token',
        'options.spec',
        'options.resource',
        'options.model',
        'options.apiVersion'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint(serviceConfig) {
            if (!serviceConfig.options?.resource || !serviceConfig.options.model) {
                throw new Error('Incomplete service config - missing "resource" or "model"');
            }
            return `https://${serviceConfig.options.resource}.openai.azure.com/openai/deployments/${serviceConfig.options.model}/chat/completions`;
        },
        requestSearchParams(serviceConfig) {
            if (!serviceConfig.options?.apiVersion) {
                throw new Error('Incomplete service config - missing "apiVersion"');
            }
            return {
                'api-version': serviceConfig.options.apiVersion
            };
        },
        requestMethod: 'POST',
        requestBodyFormat: 'json',
        healthyResponseStatusCode: 400,
        triggers: ['manual', 'schedule']
    }),
    getAiModels() {
        return {
            generate: [],
            embedding: [],
            image: []
        };
    }
};
